import { useMemo } from 'react';

import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useController, useFormContext } from 'react-hook-form';

export interface AutocompleteOption {
  label: string;
  value: string;
  group?: string;
  disabled?: boolean;
}

interface MultiSelectAutocompleteInputProps extends Omit<
  AutocompleteProps<AutocompleteOption, true, false, false>,
  'multiple' | 'renderInput' | 'value' | 'onChange' | 'options'
> {
  name: string;
  label: string;
  options: AutocompleteOption[];
  helpText?: string;
  placeholder?: string;
  includeSelectAll?: boolean;
  minSelection?: number;
  maxSelection?: number;
  listboxMaxHeight?: number;
}

export default function MultiSelectAutocompleteInput({
  name,
  label,
  options: rawOptions,
  helpText,
  placeholder,
  disabled,
  includeSelectAll = false,
  minSelection,
  maxSelection,
  listboxMaxHeight = 300,
  renderOption: renderOptionProp,
  renderGroup: renderGroupProp,
  groupBy: groupByProp,
  disableCloseOnSelect = true,
  slots: slotsProp,
  slotProps: slotPropsProp,
  ...autocompleteProps
}: MultiSelectAutocompleteInputProps) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      validate: (value: AutocompleteOption[]) => {
        if (minSelection && (value?.length ?? 0) < minSelection) {
          return `Select at least ${minSelection} option${minSelection > 1 ? 's' : ''}`;
        }
        return true;
      },
    },
  });

  const options = useMemo(() => {
    return [...rawOptions].sort((a, b) => (a.group ?? '').localeCompare(b.group ?? ''));
  }, [rawOptions]);

  const selectedValues = (field.value as AutocompleteOption[]) ?? [];
  const isAllSelected = selectedValues.length === options.length && options.length > 0;
  const isIndeterminate = selectedValues.length > 0 && selectedValues.length < options.length;
  const showSelectAll = includeSelectAll && !maxSelection && options.length > 0;

  const handleSelectAll = () => {
    field.onChange(isAllSelected ? [] : options.filter((o) => !o.disabled));
  };

  const hasGroups = rawOptions.some((o) => o.group);
  const resolvedGroupBy = groupByProp ?? (hasGroups ? (option: AutocompleteOption) => option.group ?? '' : undefined);

  const defaultRenderGroup: typeof renderGroupProp = (params) => (
    <li key={params.key}>
      <ListSubheader
        component="div"
        sx={{
          typography: 'body1',
          fontWeight: 'bold',
          lineHeight: 3,
          backgroundColor: 'background.level2',
        }}
      >
        {params.group}
      </ListSubheader>
      <ul style={{ padding: 0 }}>{params.children}</ul>
    </li>
  );

  const defaultRenderOption: typeof renderOptionProp = (props, option, { selected }) => {
    const { key, ...listItemProps } = props;
    const isAtMax = !!maxSelection && selectedValues.length >= maxSelection && !selected;

    return (
      <li
        key={key}
        {...listItemProps}
        aria-disabled={isAtMax || option.disabled}
        style={{
          ...(listItemProps.style ?? {}),
          opacity: isAtMax || option.disabled ? 0.5 : 1,
          pointerEvents: isAtMax || option.disabled ? 'none' : undefined,
        }}
      >
        <Checkbox checked={selected} disabled={isAtMax || option.disabled} sx={{ mr: 1 }} />
        {option.label}
      </li>
    );
  };

  const selectAllPaperSlot: NonNullable<typeof slotsProp>['paper'] = ({ children, ...paperProps }) => (
    <Paper {...paperProps}>
      <Box sx={{ px: 2, py: 1 }} onMouseDown={(e) => e.preventDefault()} onClick={handleSelectAll}>
        <FormControlLabel
          control={<Checkbox checked={isAllSelected} indeterminate={isIndeterminate} />}
          label="Select All"
          sx={{ width: '100%', m: 0 }}
        />
      </Box>
      <Divider />
      {children}
    </Paper>
  );

  return (
    <Box>
      {label && (
        <Typography
          component={InputLabel}
          htmlFor={name}
          variant="subtitle1"
          color="text.primary"
          fontWeight={700}
          disabled={disabled}
        >
          {label}
        </Typography>
      )}

      {helpText && (
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          {helpText}
        </Typography>
      )}

      <Autocomplete
        multiple
        disableCloseOnSelect={disableCloseOnSelect}
        disabled={disabled}
        value={selectedValues}
        options={options}
        groupBy={resolvedGroupBy}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionDisabled={(option) => !!option.disabled}
        onChange={(_event, newValue) => {
          if (maxSelection && newValue.length > maxSelection) {
            return;
          }
          field.onChange(newValue);
        }}
        slots={{
          ...slotsProp,
          ...(showSelectAll && { paper: selectAllPaperSlot }),
        }}
        slotProps={{
          ...slotPropsProp,
          listbox: {
            ...slotPropsProp?.listbox,
            sx: {
              maxHeight: listboxMaxHeight,
              padding: 0,
              ...(typeof slotPropsProp?.listbox === 'object' && 'sx' in slotPropsProp.listbox
                ? (slotPropsProp.listbox.sx as object)
                : {}),
            },
          },
        }}
        renderGroup={renderGroupProp ?? (resolvedGroupBy ? defaultRenderGroup : undefined)}
        renderOption={renderOptionProp ?? defaultRenderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            placeholder={
              selectedValues.length === 0 ? (placeholder ?? 'Select...') : isAllSelected ? 'All selected' : undefined
            }
          />
        )}
        {...autocompleteProps}
      />
    </Box>
  );
}
